import { useState, useCallback, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useFactCheckStream — consumes the SSE endpoint and returns pipeline state.
 */
export default function useFactCheckStream() {
  const [state, setState] = useState({
    running: false,
    stage: null,
    stageMessage: "",
    metadata: null,
    transcriptExcerpt: "",
    claims: [],           // detected claims (skeletons before verdicts arrive)
    verdictsByClaimId: {}, // claim_id -> verdict
    overallVerdict: null,
    overallConfidence: 0,
    overallSummary: "",
    propaganda: null,     // Agent 6 output — arrives in final `done`/`cache_hit`
    factualAccuracy: 0,
    trustScore: 0,
    processingMs: null,
    cached: false,
    error: null,
    result: null,         // final result
  });

  const sourceRef = useRef(null);

  const reset = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
    setState({
      running: false,
      stage: null,
      stageMessage: "",
      metadata: null,
      transcriptExcerpt: "",
      claims: [],
      verdictsByClaimId: {},
      overallVerdict: null,
      overallConfidence: 0,
      overallSummary: "",
      propaganda: null,
      factualAccuracy: 0,
      trustScore: 0,
      processingMs: null,
      cached: false,
      error: null,
      result: null,
    });
  }, []);

  const start = useCallback((videoUrl, forceRefresh = false) => {
    if (sourceRef.current) sourceRef.current.close();
    setState((s) => ({
      ...s,
      running: true,
      error: null,
      claims: [],
      verdictsByClaimId: {},
      overallVerdict: null,
      overallSummary: "",
      metadata: null,
      transcriptExcerpt: "",
      stage: "init",
      stageMessage: "Connecting to pipeline",
      result: null,
      cached: false,
      processingMs: null,
    }));

    const params = new URLSearchParams({
      video_url: videoUrl,
      platform: "youtube",
      force_refresh: forceRefresh ? "true" : "false",
    });
    const es = new EventSource(`${API}/v1/fact-check/stream?${params.toString()}`);
    sourceRef.current = es;

    const parseJson = (raw) => {
      try { return JSON.parse(raw); } catch { return null; }
    };

    es.addEventListener("status", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({ ...s, stage: d.stage, stageMessage: d.message || "" }));
    });

    es.addEventListener("metadata", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({ ...s, metadata: d }));
    });

    es.addEventListener("transcript", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({ ...s, transcriptExcerpt: d.excerpt || "" }));
    });

    es.addEventListener("claims_detected", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({
        ...s,
        stage: "verification",
        stageMessage: `Found ${d.count} claim(s) · verifying`,
        claims: d.claims || [],
      }));
    });

    es.addEventListener("claim_result", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({
        ...s,
        stage: "synthesis",
        stageMessage: `Verified: ${d.original_quote?.slice(0, 60) || d.claim_id}`,
        verdictsByClaimId: { ...s.verdictsByClaimId, [d.claim_id]: d },
      }));
    });

    es.addEventListener("overall", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({
        ...s,
        overallVerdict: d.overall_verdict,
        overallConfidence: d.overall_confidence,
        overallSummary: d.overall_summary,
      }));
    });

    es.addEventListener("cache_hit", (e) => {
      const d = parseJson(e.data);
      if (!d) return;
      // Normalize cached claim shape: FactCheckResult.claims are ClaimVerdict (claim_id),
      // but HomePage expects DetectedClaim shape (id) as the ordering driver.
      const normalizedClaims = (d.claims || []).map((c) => ({
        id: c.claim_id,
        original_quote: c.original_quote,
        normalized_claim: c.normalized_claim,
        priority: c.priority || 3,
        timestamp_in_video: c.timestamp_in_video || 0,
      }));
      setState((s) => ({
        ...s,
        cached: true,
        metadata: d.video_metadata,
        transcriptExcerpt: d.transcript_excerpt,
        overallVerdict: d.overall_verdict,
        overallConfidence: d.overall_confidence,
        overallSummary: d.overall_summary,
        propaganda: d.propaganda || null,
        factualAccuracy: d.factual_accuracy || 0,
        trustScore: d.trust_score || 0,
        processingMs: d.processing_time_ms,
        result: d,
        verdictsByClaimId: Object.fromEntries((d.claims || []).map((c) => [c.claim_id, c])),
        claims: normalizedClaims,
      }));
    });

    es.addEventListener("done", (e) => {
      const d = parseJson(e.data);
      setState((s) => ({
        ...s,
        running: false,
        result: d && d.claims ? d : s.result,
        processingMs: d && d.processing_time_ms != null ? d.processing_time_ms : s.processingMs,
        overallVerdict: (d && d.overall_verdict) || s.overallVerdict,
        overallConfidence: (d && d.overall_confidence) != null ? d.overall_confidence : s.overallConfidence,
        overallSummary: (d && d.overall_summary) || s.overallSummary,
        propaganda: (d && d.propaganda) || s.propaganda,
        factualAccuracy: (d && d.factual_accuracy != null) ? d.factual_accuracy : s.factualAccuracy,
        trustScore: (d && d.trust_score != null) ? d.trust_score : s.trustScore,
      }));
      es.close();
      sourceRef.current = null;
    });

    es.addEventListener("error", (e) => {
      // The DOM dispatches an "error" Event when the SSE connection closes
      // (native browser event has no `data` field). We ONLY want to handle
      // real server-sent SSE `event: error` frames here — those always carry
      // a JSON `data` payload. Native connection errors are handled by
      // `es.onerror` below, which has a `!s.running` guard so it won't
      // overwrite a completed/successful state.
      if (e.data == null || e.data === "") return;
      const d = parseJson(e.data);
      if (!d) return;
      setState((s) => ({
        ...s,
        running: false,
        error: d.message || "Stream disconnected",
      }));
      es.close();
      sourceRef.current = null;
    });

    // Fallback for EventSource native errors (e.g. network drop)
    es.onerror = () => {
      // Only mark error if we haven't received 'done'
      setState((s) => {
        if (!s.running) return s;
        return { ...s, running: false, error: s.error || "Connection lost — please retry." };
      });
      es.close();
      sourceRef.current = null;
    };
  }, []);

  return { state, start, reset };
}
